import React, { useEffect, useRef, useState, useCallback } from 'react';
import { axiosInstance } from '../App';
import { toast } from 'sonner';

/** IdleGuard
 *  Auto-logout after `idle_timeout_minutes` of no user activity, with a
 *  visible countdown for the last `idle_warning_seconds` so the user can
 *  stay signed in by simply moving the mouse.
 *
 *  Activity = mousemove, keydown, click, scroll, touchstart.
 *  The threshold values come from localStorage (set on login). If absent,
 *  defaults match the backend defaults (30 min / 120 sec).
 */
const ACTIVITY_EVENTS = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'];

const IdleGuard = () => {
  const [warning, setWarning] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const idleStart = useRef(Date.now());
  const tickRef = useRef(null);

  const idleMinutes = Math.max(1, parseInt(localStorage.getItem('idle_timeout_minutes') || '30', 10));
  const warnSec = Math.max(5, parseInt(localStorage.getItem('idle_warning_seconds') || '120', 10));

  const reset = useCallback(() => {
    idleStart.current = Date.now();
    if (warning) setWarning(false);
  }, [warning]);

  const stayLoggedIn = useCallback(() => {
    idleStart.current = Date.now();
    setWarning(false);
  }, []);

  const doLogout = useCallback(async () => {
    try { await axiosInstance.post('/auth/logout'); } catch (_) {}
    ['token', 'user', 'refresh_token', 'session_id'].forEach(k => localStorage.removeItem(k));
    toast.info('Signed out due to inactivity');
    setTimeout(() => { window.location.href = '/login'; }, 400);
  }, []);

  useEffect(() => {
    // Only run when authenticated
    if (!localStorage.getItem('token')) return undefined;
    const handler = () => reset();
    ACTIVITY_EVENTS.forEach(e => window.addEventListener(e, handler, { passive: true }));
    return () => ACTIVITY_EVENTS.forEach(e => window.removeEventListener(e, handler));
  }, [reset]);

  useEffect(() => {
    if (!localStorage.getItem('token')) return undefined;
    const idleMs = idleMinutes * 60 * 1000;
    const warnMs = warnSec * 1000;
    tickRef.current = setInterval(() => {
      const elapsed = Date.now() - idleStart.current;
      const remaining = idleMs - elapsed;
      if (remaining <= 0) {
        clearInterval(tickRef.current);
        doLogout();
      } else if (remaining <= warnMs) {
        setWarning(true);
        setSecondsLeft(Math.ceil(remaining / 1000));
      } else if (warning) {
        setWarning(false);
      }
    }, 1000);
    return () => clearInterval(tickRef.current);
  }, [idleMinutes, warnSec, warning, doLogout]);

  if (!warning) return null;

  return (
    <div
      data-testid="idle-warning-modal"
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm"
    >
      <div className="bg-white rounded-2xl shadow-2xl border border-amber-200 max-w-md w-[92%] p-6">
        <h3 className="text-xl font-bold text-amber-700 mb-2">Still there?</h3>
        <p className="text-sm text-slate-700 mb-4">
          You will be signed out in <span className="font-bold text-red-600" data-testid="idle-countdown">{secondsLeft}s</span> due to inactivity.
        </p>
        <div className="flex gap-3">
          <button
            data-testid="idle-stay-button"
            onClick={stayLoggedIn}
            className="flex-1 px-4 py-2 rounded-lg bg-indigo-600 text-white font-semibold hover:bg-indigo-700"
          >
            Keep me signed in
          </button>
          <button
            data-testid="idle-logout-button"
            onClick={doLogout}
            className="px-4 py-2 rounded-lg border border-slate-300 hover:bg-slate-50"
          >
            Log out
          </button>
        </div>
      </div>
    </div>
  );
};

export default IdleGuard;
